import React from "react";
import { Tooltip as AntTooltip } from "antd";
import PropTypes from "prop-types";

import "antd/es/tooltip/style/css";

import styles from "./tooltip.css";

/**
 * Renders a tooltip element that displays additional information on hover.
 */

export function Tooltip(props) {
  const {
    children,
    content,
    trigger = TOOLTIP_TRIGGER.HOVER,
    type = TOOLTIP_TYPES.MEDIUM,
    position = TOOLTIP_POSITION.TOP,
    onVisibleChange,
    visible = false,
    getContainer = () => document.body,
    destroyOnClose = true,
    arrowPointAtCenter = false,
    className,
    alwaysVisible,
    mouseEnterDelay, // delay showing tooltip on mouse enter (in seconds default 0.1)
    mouseLeaveDelay, // delay hiding tooltip on mouse leave (in seconds default 0.1),
    zIndex
  } = props;

  let tooltipProps = {};
  if (alwaysVisible) {
    tooltipProps.visible = true;
  }

  const getPosition = (position) => {
    const rtlPosition = {
      top: "top",
      right: "left",
      left: "right",
      bottom: "bottom",
      topLeft: "topRight",
      topRight: "topLeft",
      bottomLeft: "bottomRight",
      bottomRight: "bottomLeft",
      leftTop: "rightTop",
      leftBottom: "rightBottom",
      rightTop: "leftTop",
      rightBottom: "leftBottom"
    };
    return window.isRTL ? rtlPosition[position] : position;
  };

  return (
    <AntTooltip
      title={content}
      type={type}
      placement={getPosition(position)}
      overlayClassName={`${styles.tooltip} ${className}`}
      defaultVisible={visible}
      onVisibleChange={onVisibleChange}
      getPopupContainer={getContainer}
      destroyTooltipOnHide={destroyOnClose}
      mouseEnterDelay={mouseEnterDelay}
      mouseLeaveDelay={mouseLeaveDelay}
      trigger={trigger}
      arrowPointAtCenter={arrowPointAtCenter}
      {...tooltipProps}
      zIndex={zIndex}
    >
      {children}
    </AntTooltip>
  );
}

export const TOOLTIP_TYPES = {
  SMALL: "small",
  MEDIUM: "medium",
  LARGE: "large"
};

export const TOOLTIP_POSITION = {
  TOP: "top",
  LEFT: "left",
  RIGHT: "right",
  BOTTOM: "bottom",
  TOP_LEFT: "topLeft",
  TOP_RIGHT: "topRight",
  BOTTOM_LEFT: "bottomLeft",
  BOTTOM_RIGHT: "bottomRight",
  LEFT_TOP: "leftTop",
  LEFT_BOTTOM: "leftBottom",
  RIGHT_TOP: "rightTop",
  RIGHT_BOTTOM: "rightBottom"
};

export const TOOLTIP_TRIGGER = {
  HOVER: "hover",
  FOCUS: "focus",
  CLICK: "click"
};

Tooltip.propTypes = {
  /**
   * The content to be rendered inside the tooltip.
   */
  children: PropTypes.any,
  /**
   * The content to be displayed in the tooltip.
   */
  content: PropTypes.any,
  /**
   * The type of the tooltip (e.g., "large", "medium", "small").
   */
  type: PropTypes.string,
  /**
   * Position of the tooltip relative to the target element.
   */
  position: PropTypes.string,
  /**
   * Specifies whether the tooltip is currently visible.
   */
  visible: PropTypes.bool,
  /**
   * Callback function invoked when the visibility of the tooltip changes.
   */
  onVisibleChange: PropTypes.func,
  /**
   * Function that returns the DOM container for the tooltip.
   */
  getContainer: PropTypes.func,
  /**
   * Specifies whether to destroy the tooltip content when it is closed.
   */
  destroyOnClose: PropTypes.bool,
  /**
   * Specifies whether the tooltip is always visible (not triggered by hover).
   */
  tooltipVisible: PropTypes.bool,
  /**
   * Specifies whether the tooltip pointer to align at center.
   */
  arrowPointAtCenter: PropTypes.bool,
  /**
   * Additional class name(s) for custom styling.
   */
  className: PropTypes.string,
  /**
   * Specifies whether the tooltip is always visible, regardless of trigger events.
   */
  alwaysVisible: PropTypes.bool,
  /**
   * Delay in milliseconds before showing the tooltip on mouse enter.
   */
  mouseEnterDelay: PropTypes.number,
  /**
   * Delay in milliseconds before hiding the tooltip on mouse leave.
   */
  mouseLeaveDelay: PropTypes.number,
  /**
   * Event trigger for showing and hiding the tooltip (e.g., "hover", "click" , "focus").
   */
  trigger: PropTypes.string,
  /**
   * The z-index of the tooltip.
   */
  zIndex: PropTypes.number
};

Tooltip.defaultProps = {
  trigger: TOOLTIP_TRIGGER.HOVER,
  type: TOOLTIP_TYPES.MEDIUM,
  position: TOOLTIP_POSITION.TOP,
  visible: false,
  getContainer: () => document.body,
  destroyOnClose: true,
  arrowPointAtCenter: false
};
